import { useState, useEffect, useRef } from "react";
import { SkyDropdown } from "@skypointcloud/skypoint-ui";
import { useDispatch, useSelector } from "react-redux";
import { IRootReducerState } from "../../../../store/IRootReducer";
import {
  instanceChange as instanceChangeAction,
  tenantChange as tenantChangeAction,
} from "../../../../store/slices/authSlice";
import { updateDashboardCurrentStitchId } from "../../../../store/slices/dashboardSlice";
import { changedInstanceLogCreate } from "../../../../store/instanceManagement/instanceService";
import { useCurrentUser } from "../hooks";
import { authService } from "../../../../utils/authService";

interface TenantInstanceSwitcherProps {
  onContextChange?: () => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

export function TenantInstanceSwitcher({
  onContextChange,
  onLoadingChange,
}: TenantInstanceSwitcherProps) {
  const dispatch = useDispatch();

  // Get user data from hook
  const { currentUser } = useCurrentUser();

  // Get data directly from Redux (already loaded by header)
  const tenantsData = useSelector(
    (state: IRootReducerState) => state.tenantsReducer?.tenantsData || {},
  );
  const { tenants, selectedTenant } = tenantsData;
  const instancesData = useSelector(
    (state: IRootReducerState) => state.instancesReducer?.instancesData || {},
  );
  const { instances, selectedInstanceName, selectedInstanceId } = instancesData;

  // Derive selectedTenantBusinessName from tenants array
  const selectedTenantBusinessName =
    tenants?.find((t: { id: string; businessname: string }) => t.id === selectedTenant?.id)
      ?.businessname || "";

  // Context change state
  const [changedTenant, setChangedTenant] = useState<string>("");
  const [changedInstance, setChangedInstance] = useState<string>("");
  const [disabledMenu, setDisabledMenu] = useState<boolean>(false);
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  // Reset Redux state on context change
  const resetReduxState = () => {
    dispatch(updateDashboardCurrentStitchId(undefined));
  };

  const redirectToUrl = (instanceId: string) => {
    const url = window.location.href;
    const arr = url.split("/");
    const newUrl = `${arr[0]}//${arr[2]}?instanceid=${instanceId}`;
    redirectTimeoutRef.current = setTimeout(() => {
      window.location.href = newUrl;
    }, 100);
  };

  const currentTenantName = changedTenant || selectedTenantBusinessName;
  const currentInstanceName = changedInstance || selectedInstanceName;

  // When changing tenant, derive instances from the tenants array to stay in sync
  // This prevents showing mismatched tenant/instances during the switch
  const currentTenantForInstances = changedTenant
    ? tenants?.find((t: { id: string; businessname: string }) => t.businessname === changedTenant)
    : null;

  const handleTenantSelect = async (tenantId: string, tenantName: string) => {
    if (tenantName === currentTenantName) {
      return;
    }

    // Show loading overlay FIRST (in parent) before menu closes
    onLoadingChange?.(true);
    setDisabledMenu(true);
    setChangedTenant(tenantName);
    onContextChange?.();
    resetReduxState();

    await changedInstanceLogCreate("Tenant");
    dispatch(tenantChangeAction());

    const userTenants = currentUser?.tenants as
      | Array<{ tenantid: string; instances?: Array<{ instanceid: string }> }>
      | undefined;
    const userTenantAccess =
      !currentUser?.isPlatformAdmin && userTenants
        ? userTenants.find((x) => x.tenantid === tenantId)
        : null;

    const instanceId =
      userTenantAccess?.instances?.[0]?.instanceid ||
      tenants?.find((x: { id: string; instances: Array<{ id: string }> }) => x.id === tenantId)
        ?.instances[0]?.id;

    if (instanceId) {
      // Save tenant and instance to localStorage before redirecting
      authService.saveTenantId(tenantId);
      authService.saveInstanceId(instanceId);
      redirectToUrl(instanceId);
    }
  };

  const handleInstanceSelect = async (instanceId: string, instanceName: string) => {
    if (instanceName === currentInstanceName) {
      return;
    }

    // Show loading overlay FIRST (in parent) before menu closes
    onLoadingChange?.(true);
    setDisabledMenu(true);
    setChangedInstance(instanceName);
    onContextChange?.();
    resetReduxState();

    await changedInstanceLogCreate("Instance");
    dispatch(instanceChangeAction());

    // Save instance to localStorage before redirecting
    authService.saveInstanceId(instanceId);
    redirectToUrl(instanceId);
  };

  // Prepare dropdown options
  const tenantOptions =
    tenants?.map((tenant: { id: string; businessname: string }) => ({
      value: tenant.id,
      text: tenant.businessname,
    })) || [];

  // Use instances from the selected tenant's nested array to stay scoped.
  // Only fall back to flat Redux instances if tenants don't have nested instances.
  const selectedTenantObj = tenants?.find(
    (t: { id: string; businessname: string }) =>
      t.businessname === currentTenantName || t.id === selectedTenant?.id,
  );
  const instancesToUse =
    currentTenantForInstances?.instances || selectedTenantObj?.instances || instances || [];

  const sortedInstances = [...instancesToUse].sort(
    (a: { displayname?: string; id: string }, b: { displayname?: string; id: string }) =>
      (a.displayname || a.id).toLowerCase().localeCompare((b.displayname || b.id).toLowerCase()),
  );

  const instanceOptions = sortedInstances.map((instance: { id: string; displayname?: string }) => ({
    value: instance.id,
    text: instance.displayname || instance.id,
  }));

  const selectedTenantKey = tenants?.find(
    (t: { id: string; businessname: string }) => t.businessname === currentTenantName,
  )?.id;
  const selectedInstanceKey =
    instancesToUse?.find((i: { id: string }) => i.id === selectedInstanceId)?.id ||
    instancesToUse?.find(
      (i: { id: string; displayname?: string }) => i.displayname === currentInstanceName,
    )?.id;

  const hasMultipleTenants = (tenants?.length ?? 0) > 1;
  const hasMultipleInstances = instancesToUse.length > 1;
  const hasInstances = instancesToUse.length > 0;

  // Show tenant dropdown if there are multiple tenants available
  const showTenantDropdown = hasMultipleTenants;
  // Show instance dropdown if there are instances to select from
  const showInstanceDropdown = hasInstances;

  // Don't render if there's nothing to show
  if (!showTenantDropdown && !showInstanceDropdown) {
    return null;
  }

  return (
    <div className="p-4 border-b border-gray-200 flex flex-col gap-3">
      {showTenantDropdown && (
        <SkyDropdown
          label="Tenant"
          options={tenantOptions}
          selectedOptions={selectedTenantKey ? [selectedTenantKey] : []}
          onOptionSelect={(values) => {
            const tenantId = values[0];
            const tenant = tenants?.find(
              (t: { id: string; businessname: string }) => t.id === tenantId,
            );
            if (tenant) {
              handleTenantSelect(tenantId, tenant.businessname);
            }
          }}
          disabled={disabledMenu}
        />
      )}
      {showInstanceDropdown && (
        <SkyDropdown
          label="Instance"
          options={instanceOptions}
          selectedOptions={selectedInstanceKey ? [selectedInstanceKey] : []}
          onOptionSelect={(values) => {
            const instanceId = values[0];
            const instance = instancesToUse?.find(
              (i: { id: string; displayname?: string }) => i.id === instanceId,
            );
            if (instance) {
              handleInstanceSelect(instanceId, instance.displayname || instance.id);
            }
          }}
          disabled={disabledMenu || !hasMultipleInstances}
        />
      )}
    </div>
  );
}
